import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth-context';
import { hasArea } from '../lib/permissions';
import { Badge, Card, CardHeader, PageHeader, Spinner } from '../components/ui';
import { date, money, percent } from '../lib/format';
import { useTaxReserve } from '../lib/tax-reserve-api';
import { unpostedQuery, unpostedTotal } from '../lib/accounting-api';
import type { SettingsResponse } from './settings-types';
import {
  DOCUMENT_LANGUAGE_LABELS,
  VAT_TREATMENT_LABELS,
  type CalendarEntry,
  type OpenItem,
  type SetupReadiness,
  type SetupStepCode,
  type VatReadiness,
} from '../lib/compliance-api';

/**
 * `GET /api/health` gövdesi: `{ status: 'ok' | 'degraded', database: boolean,
 * redis: boolean }`. Uç kimlik istemez ve **bozuk durumda bile HTTP 200 döner**
 * — ok/degraded ayrımı yalnızca gövdeden okunur, durum kodundan değil.
 */
interface HealthResponse {
  status: string;
  database: boolean;
  redis: boolean;
}

function StatusDot({ ok }: { ok: boolean }) {
  return <Badge tone={ok ? 'ok' : 'danger'}>{ok ? 'çalışıyor' : 'sorunlu'}</Badge>;
}

export function DashboardPage() {
  const { user } = useAuth();
  const canReadSettings = hasArea(user, 'settings');
  const canReadCompliance = hasArea(user, 'compliance');
  const canReadReports = hasArea(user, 'reports');
  const canReadAccounting = hasArea(user, 'accounting');

  /*
   * Sağlık ucu kimlik istemez ve bozuk durumda bile HTTP 200 döner —
   * ok/degraded ayrımı GÖVDEDEN okunur, durum kodundan değil.
   */
  const health = useQuery({
    queryKey: ['health'],
    queryFn: () => api.get<HealthResponse>('/health', { anonymous: true }),
    refetchInterval: 60_000,
  });

  const settings = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get<SettingsResponse>('/settings'),
    enabled: canReadSettings,
  });

  /*
   * ⚠ "YAZILIM BUGÜN NEYİ YAPAMIYOR" — ve bu soru panoya AİT.
   *
   * Zorunlu ibare defteri hangi muamelenin faturalanabileceğini belirliyor.
   * Operatör bunu daha önce ancak faturayı kesmeyi DENEYİP bloke ediciye
   * takıldığında öğreniyordu; oysa bilgi teklif hazırlanırken gerekiyor.
   * Muhasebecisi olmayan bir mükellef için bu cümleyi kuracak başka kimse yok.
   */
  const readiness = useQuery({
    queryKey: ['vat-readiness'],
    queryFn: () => api.get<VatReadiness>('/compliance/vat-readiness'),
    enabled: canReadCompliance,
  });

  const values = settings.data?.values;
  const identity = values
    ? {
        legalName: String(values.company_legal_name ?? ''),
        tradeName: String(values.company_trade_name ?? ''),
        vat: String(values.company_vat_number ?? ''),
        city: String(values.company_city ?? ''),
        region: String(values.company_exploitation_region ?? ''),
        iban: String(values.company_iban ?? ''),
      }
    : null;

  const REGION_LANGUAGE: Record<string, string> = {
    FLANDERS: 'Hollandaca',
    WALLONIA: 'Fransızca',
    BRUSSELS: 'Hollandaca veya Fransızca',
  };

  return (
    <>
      <PageHeader
        title={`Merhaba, ${user?.name ?? ''}`}
        description="Fatura, alış, gider, KDV beyanı ve e-fatura tek yerde. Panodaki ilk rakam kenara ayrılacak tutardır."
      />

      {/*
        ⚠ KURULUM EKSİKLERİ HER ŞEYİN ÜSTÜNDE — kenara ayrılacak tutarın bile.
        Bunlar bloke edicidir: eksikken pano üzerindeki hiçbir rakamın bir
        anlamı yok, çünkü henüz tek bir belge kesilememiştir.
      */}
      {canReadCompliance && <SetupBand />}
      {canReadReports && <TaxReserveBand />}
      {canReadCompliance && <ComplianceBand canReadAccounting={canReadAccounting} />}

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader title="Sistem durumu" />
          <div className="px-4 py-3">
            {health.isPending ? (
              <Spinner />
            ) : health.isError ? (
              <p className="text-sm text-[var(--c-danger)]">Sunucuya ulaşılamıyor.</p>
            ) : (
              <dl className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <dt className="text-[var(--c-ink-soft)]">API</dt>
                  <dd>
                    <StatusDot ok={health.data.status === 'ok'} />
                  </dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-[var(--c-ink-soft)]">Veritabanı</dt>
                  <dd>
                    <StatusDot ok={health.data.database} />
                  </dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-[var(--c-ink-soft)]">Redis</dt>
                  <dd>
                    <StatusDot ok={health.data.redis} />
                  </dd>
                </div>
              </dl>
            )}
          </div>
        </Card>

        {canReadSettings && (
          <Card>
            <CardHeader title="Mükellef" description="Ayarlar sayfasından değiştirilir." />
            <div className="px-4 py-3">
              {settings.isPending ? (
                <Spinner />
              ) : identity ? (
                <dl className="space-y-1.5 text-sm">
                  <Row label="Hukuki ad" value={identity.legalName} />
                  <Row label="Ticari ad" value={identity.tradeName} />
                  <Row label="KDV numarası" value={identity.vat} />
                  <Row label="İşletme merkezi" value={identity.city} />
                  <Row
                    label="Fatura dili"
                    value={REGION_LANGUAGE[identity.region] ?? identity.region}
                  />
                </dl>
              ) : (
                <p className="text-sm text-[var(--c-ink-soft)]">Ayarlar okunamadı.</p>
              )}
            </div>
          </Card>
        )}
      </div>

      {canReadCompliance && readiness.data && (
        <>
          {readiness.data.blockedInPrimaryLanguage.length > 0 && (
            <Card className="mt-4 border-[var(--c-warn)]">
              <div className="px-4 py-3">
                <p className="text-sm font-medium text-[var(--c-warn)]">
                  Bu işlemler bugün faturalanamıyor
                </p>
                {/*
                  ⚠ BU BİR KULLANICI HATASI DEĞİL, YAZILIMIN EKSİĞİDİR — ve öyle
                  yazılmalı. Operatör "yanlış bir şey mi yaptım" diye aramasın:
                  zorunlu ibarenin resmî metni elde olmadığı için o muamele
                  kapalıdır ve metin UYDURULAMAZ (CJEU C-247/21 birebir yazım
                  ister, sonradan eklenmesine de izin vermez).
                */}
                <ul className="mt-2 space-y-1">
                  {readiness.data.blockedInPrimaryLanguage.map((item) => (
                    <li key={item.treatment} className="text-xs text-[var(--c-ink-soft)]">
                      <span className="font-medium">
                        {VAT_TREATMENT_LABELS[item.treatment] ?? item.treatment}
                      </span>
                      {' — zorunlu ibarenin '}
                      {DOCUMENT_LANGUAGE_LABELS[readiness.data?.primaryLanguage ?? ''] ??
                        'yasal dildeki'}
                      {' metni kaynaktan doğrulanmadı.'}
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-xs text-[var(--c-ink-soft)]">
                  Bu bir ayar eksiği değildir: ibare uydurulamaz. Böyle bir iş faturalanacaksa
                  metnin resmî yayından okunması gerekir.
                </p>
              </div>
            </Card>
          )}

          {readiness.data.conditionalMentions.length > 0 && (
            <Card className="mt-4">
              <div className="px-4 py-3">
                <p className="text-sm font-medium">Koşulu değerlendirilemeyen ibareler</p>
                {/*
                  ⚠ BU BİR YAPILACAKLAR LİSTESİ DEĞİL, BİR BEYANDIR. Yazılım
                  koşulu veriden değerlendiremediği için bir KARAR vermiş
                  durumda ve sonucunu mükellef üstleniyor — o hâlde kararı
                  görmesi gerekir.
                */}
                {readiness.data.conditionalMentions.map((item) => (
                  <div key={item.key} className="mt-2 text-xs text-[var(--c-ink-soft)]">
                    <p>
                      <span className="font-medium">
                        {VAT_TREATMENT_LABELS[item.key] ?? item.key}
                      </span>
                      {' — hüküm ibareyi şu koşulla sınırlıyor: '}
                      <span className="italic">&laquo;{item.condition}&raquo;</span>
                    </p>
                    <p className="mt-0.5">{item.rationale}</p>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}

      {/*
        ⚠ AYRI BİR IBAN KARTI ARTIK YOK. Aynı eksik iki yerde iki ayrı hesapla
        (biri `/settings` gövdesinden, biri sunucunun hazırlık ucundan)
        gösteriliyordu; ayrıştıkları gün pano kendi kendisiyle çelişirdi. IBAN
        bugün `SetupBand` içindeki bir adım ve gerekçesi sunucudan geliyor.
      */}
    </>
  );
}

/**
 * İLK GÜN: YAZILIM HENÜZ NEYİ YAPAMAZ.
 *
 * ⚠⚠ ÜRETİMDE TAM DA BU OLDU — sıfır cari, sıfır kalem, hiç mali yıl. Operatör
 * ilk faturayı kesmeye çalıştığında bir doğrulama hatası görüyor, ama NEYİN
 * önce yapılması gerektiğini söyleyen hiçbir ekran yoktu.
 *
 * ⚠ HER ŞEY TAMAMSA KART KAYBOLUR. Kalıcı bir "kurulum" kartı, birkaç gün
 * sonra göz tarafından atlanan bir dekora dönüşür.
 *
 * ⚠ METİNLER SUNUCUDAN GELİR (`blocks`, `reason`): bir eksiğin neyi engellediği
 * ve hukuki gerekçesi kuralın kendisiyle aynı dosyada durmalı.
 */
function SetupBand() {
  const setup = useQuery({
    queryKey: ['compliance', 'setup-readiness'],
    queryFn: () => api.get<SetupReadiness>('/compliance/setup-readiness'),
  });

  if (setup.isPending || setup.isError) return null;
  const eksikler = setup.data.steps.filter((s) => !s.done);
  if (eksikler.length === 0) return null;

  return (
    <Card className="mb-4 border-[var(--c-warn)]">
      <CardHeader
        title="Kurulum tamamlanmadı"
        description={
          setup.data.canIssueFirstInvoice
            ? 'Fatura kesilebilir; aşağıdakiler başka işleri engelliyor.'
            : 'Bu eksikler kapanmadan ilk fatura kesilemez.'
        }
      />
      <ol className="space-y-3 px-4 pb-4 text-sm">
        {eksikler.map((s) => (
          <li key={s.code}>
            <p className="font-medium text-[var(--c-warn)]">{s.blocks}</p>
            <p className="mt-0.5 text-xs text-[var(--c-ink-soft)]">{s.reason}</p>
            <Link className="mt-1 inline-block text-xs text-[var(--c-brand)] underline" to={s.path}>
              {SETUP_ACTIONS[s.code]}
            </Link>
          </li>
        ))}
      </ol>
    </Card>
  );
}

/** ⚠ YALNIZ DÜĞME METNİ panelde: gerekçe ve engel sunucudan geliyor. */
const SETUP_ACTIONS: Record<SetupStepCode, string> = {
  CONTACT: 'Müşteri ekleyin',
  ITEM: 'Katalog kalemi ekleyin',
  IBAN: 'Ayarlarda IBAN tanımlayın',
  FISCAL_YEAR: 'Mali yılı açın',
};

/**
 * PANODAKİ EN BÜYÜK RAKAM: kenara ayrılacak tutar.
 *
 * ⚠ BURASI BİR ÖZETTİR, hesap değil. Tek bir sayı ve tek bir cümle; ayrıntı,
 * uyarılar ve "rakam nereden geldi" dökümü `/reports`ta. Panoya döküm koymak,
 * her açılışta okunmayan bir tablo demekti; okunmayan tabloda yanlış bir rakam
 * da fark edilmez.
 *
 * ⚠ HATA DURUMUNDA SESSİZCE KAYBOLUR. Pano bir kontrol ekranıdır: tahmin ucu
 * düşse bile öteki kartlar (sağlık, kimlik, faturalanamayan muameleler)
 * çalışmaya devam etmeli.
 */
function TaxReserveBand() {
  const reserve = useTaxReserve();
  if (reserve.isPending || reserve.isError) return null;

  const data = reserve.data;
  return (
    <Card className="mb-4">
      <Link to="/reports" className="block px-4 py-4 hover:bg-[var(--c-surface-alt)]">
        <p className="text-sm text-[var(--c-ink-soft)]">
          {data.incomeYear} için kenara ayırmanız gereken tutar
        </p>
        <p className="mt-1 text-3xl font-semibold tabular-nums">
          {money(data.amounts.totalReserve)}
        </p>
        <p className="mt-1 text-xs text-[var(--c-ink-faint)]">
          {data.reservePctOfProfit === null
            ? 'Bu yıl için kâr görünmüyor — ayrıntı için tıklayın.'
            : `Kârın ${percent(data.reservePctOfProfit)}’i · sosyal prim ${money(
                data.amounts.socialTotal,
              )} + gelir vergisi artışı ${money(data.amounts.incomeTaxIncrement)}`}
        </p>
      </Link>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-[var(--c-ink-soft)]">{label}</dt>
      <dd className="text-right font-medium">{value || '—'}</dd>
    </div>
  );
}

/**
 * PANODAKİ İKİNCİ RAKAM: NE ZAMAN, NE VERİLECEK.
 *
 * ⚠⚠ SUNUCU BUNU HESAPLIYORDU AMA PANO SORMUYORDU. Uyum takvimi ve "biriken
 * iş" listesi `/compliance` sayfasında duruyordu; muhasebecisi olmayan bir
 * operatörün o sayfayı düzenli açmasını beklemek, tam da yazılımın onun yerine
 * yapması gereken şeyi ondan istemektir. Sonucu: süresi dolan bir beyan hiçbir
 * yerde görünmüyordu.
 *
 * ⚠ YALNIZ GECİKMİŞ VE YAKLAŞAN GÖSTERİLİR. Tüm takvimi panoya dökmek, her
 * gün bakılan bir ekranı okunmaz yapardı — ve okunmayan uyarı, olmayan
 * uyarıdır.
 *
 * ⚠ TARİHİ DOĞRULANMAMIŞ KALEMLER PANOYA GELMEZ (`basis: 'UNVERIFIED'`):
 * yanlış bir son gün göstermek, hiç göstermemekten kötüdür. Onlar takvim
 * sayfasında kendi kartında duruyor.
 */
function ComplianceBand({ canReadAccounting }: { canReadAccounting: boolean }) {
  const calendar = useQuery({
    queryKey: ['compliance', 'calendar'],
    queryFn: () => api.get<CalendarEntry[]>('/compliance/calendar'),
  });
  const openItems = useQuery({
    queryKey: ['compliance', 'open-items'],
    queryFn: () => api.get<OpenItem[]>('/compliance/open-items'),
  });
  /*
   * ⚠ AYRI ALAN, AYRI KAPI. "Deftere girmemiş belge" sayacı `accounting`
   * alanına ait; uyum alanı açık ama defter alanı kapalı bir kullanıcıya
   * sorulursa 403 alır ve kart hiç görünmezdi. Sorgu alan izniyle kapatılıyor,
   * kartın geri kalanı çalışmaya devam ediyor.
   */
  const unposted = useQuery({
    ...unpostedQuery(),
    enabled: canReadAccounting,
  });

  if (calendar.isError || openItems.isError) return null;

  const acil = (calendar.data ?? [])
    .filter((e) => !e.done && e.basis !== 'UNVERIFIED')
    .filter((e) => e.urgency === 'OVERDUE' || e.urgency === 'DUE_SOON')
    .sort((a, b) => (a.dueDate ?? '').localeCompare(b.dueDate ?? ''))
    .slice(0, 4);

  const u = unposted.data;
  const kayitsiz = unpostedTotal(u);
  const birikenler = openItems.data ?? [];

  if (acil.length === 0 && birikenler.length === 0 && kayitsiz === 0) return null;

  return (
    <Card className="mb-4">
      <CardHeader
        title="Yaklaşan yükümlülükler ve biriken iş"
        description="Takvimin tamamı Uyum takvimi sayfasında; burada yalnız gecikmiş ve yaklaşanlar var."
      />
      <div className="space-y-3 px-4 py-3 text-sm">
        {acil.length > 0 && (
          <ul className="space-y-1">
            {acil.map((e) => (
              <li key={`${e.kind}-${e.periodKey}`} className="flex flex-wrap items-center gap-2">
                <Badge tone={e.urgency === 'OVERDUE' ? 'danger' : 'warn'}>
                  {e.urgency === 'OVERDUE' ? 'gecikmiş' : 'yaklaşıyor'}
                </Badge>
                <span>
                  {e.label} · {e.periodKey}
                  {e.dueDate !== null && (
                    <span className="text-[var(--c-ink-soft)]"> · son gün {date(e.dueDate)}</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}

        {(birikenler.length > 0 || kayitsiz > 0) && (
          <div className="flex flex-wrap gap-2">
            {birikenler.map((i) => (
              <Badge key={i.code} tone="warn">
                {i.label}: {i.count}
              </Badge>
            ))}
            {kayitsiz > 0 && <Badge tone="warn">Deftere girmemiş belge: {kayitsiz}</Badge>}
          </div>
        )}

        <div className="flex flex-wrap gap-3 text-xs">
          <Link className="text-[var(--c-brand)] hover:underline" to="/compliance">
            Uyum takvimi
          </Link>
          <Link className="text-[var(--c-brand)] hover:underline" to="/vat-return">
            KDV beyanı
          </Link>
          <Link className="text-[var(--c-brand)] hover:underline" to="/accounting">
            Defter
          </Link>
        </div>
      </div>
    </Card>
  );
}
